import { useMemo, useState } from "react";
import type { SessionResponseDto } from "@madamuh/domain";
import { useNavigate } from "react-router-dom";
import { getTenantLabels, GENERIC_TENANT_LABELS } from "../../lib/tenantLabels";
import { useSessionBootstrap } from "../../session/SessionBootstrapProvider";
import { canUseFamilyMode } from "../../session/modeRouting";
import {
  AdminIcon,
  HomeIcon,
  TeacherIcon
} from "../icons/AppIcons";
import type { ProtectedDrawerActionItem } from "./ProtectedRoleDrawer";

export type ProtectedAppArea = "family" | "teacher" | "admin";

export function resolveDrawerViewerName(session: SessionResponseDto, currentArea: ProtectedAppArea): string {
  const roles = session.member?.roles ?? [];
  if (currentArea === "family" && roles.includes("student") && !roles.includes("parent") && session.viewerProfile.selfStudents.length === 1) {
    return session.viewerProfile.selfStudents[0]?.displayName ?? session.viewerProfile.accountDisplayName;
  }
  return session.viewerProfile.accountDisplayName;
}

export function resolveDrawerViewerRoleLabel(session: SessionResponseDto, currentArea: ProtectedAppArea): string {
  const labels = getTenantLabels(session.tenant.uiLabelProfile);
  const roles = session.member?.roles ?? [];
  if (currentArea === "teacher") return labels.teacher;
  if (currentArea === "admin") return "管理者";
  if (roles.includes("student") && roles.includes("parent")) return `${labels.student}・保護者`;
  if (roles.includes("student")) return labels.student;
  return "保護者";
}

type UseProtectedRoleDrawerOptions = {
  tenantSlug: string;
  session: SessionResponseDto | null;
  currentArea: ProtectedAppArea;
};

export function useProtectedRoleDrawer({
  tenantSlug,
  session,
  currentArea
}: UseProtectedRoleDrawerOptions): {
  actionItems: ProtectedDrawerActionItem[];
  errorMessage: string | null;
  viewerName: string;
  viewerRoleLabel: string;
} {
  const navigate = useNavigate();
  const { switchAppMode } = useSessionBootstrap();
  const [switchingTarget, setSwitchingTarget] = useState<ProtectedAppArea | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  async function handleSwitchMode(target: ProtectedAppArea) {
    if (target === currentArea) {
      return;
    }

    setErrorMessage(null);
    setSwitchingTarget(target);

    try {
      await switchAppMode(tenantSlug, target === "family" ? "family" : "operator");

      if (target === "family") {
        navigate(`/t/${tenantSlug}/home`, { replace: true });
        return;
      }

      if (target === "teacher") {
        navigate(`/t/${tenantSlug}/teacher`, { replace: true });
        return;
      }

      navigate(`/t/${tenantSlug}/teacher/admin`, { replace: true });
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "ロールの切り替えに失敗しました。時間をおいてもう一度お試しください。"
      );
    } finally {
      setSwitchingTarget(null);
    }
  }

  const actionItems = useMemo(() => {
    const items: ProtectedDrawerActionItem[] = [];

    if (canUseFamilyMode(session)) {
      items.push({
        key: "family",
        label: "ホーム",
        icon: <HomeIcon size={20} />,
        current: currentArea === "family",
        disabled: currentArea === "family" || switchingTarget !== null,
        ...(switchingTarget === "family" ? { supportText: "切替中..." } : {}),
        onSelect: () => void handleSwitchMode("family")
      });
    }

    if (session?.member?.roles.includes("teacher") || session?.member?.roles.includes("admin")) {
      items.push({
        key: "teacher",
        label: `${tenantLabels.teacher}に切り替え`,
        icon: <TeacherIcon size={20} />,
        current: currentArea === "teacher",
        disabled: currentArea === "teacher" || switchingTarget !== null,
        ...(switchingTarget === "teacher" ? { supportText: "切替中..." } : {}),
        onSelect: () => void handleSwitchMode("teacher")
      });
    }

    if (session?.member?.roles.includes("admin")) {
      items.push({
        key: "admin",
        label: "管理者に切り替え",
        icon: <AdminIcon size={20} />,
        current: currentArea === "admin",
        disabled: currentArea === "admin" || switchingTarget !== null,
        ...(switchingTarget === "admin" ? { supportText: "切替中..." } : {}),
        onSelect: () => void handleSwitchMode("admin")
      });
    }

    return items;
  }, [currentArea, session, switchingTarget, tenantLabels.teacher]);

  return {
    actionItems,
    errorMessage,
    viewerName: session ? resolveDrawerViewerName(session, currentArea) : "利用者",
    viewerRoleLabel: session ? resolveDrawerViewerRoleLabel(session, currentArea) : ""
  };
}
